import { captureMessage } from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { Field, Form, Formik, useFormikContext } from 'formik';
import {
  useCreateETSTicketMutation,
  usePatchETSTicketStatusMutation,
} from 'gcs-common/clients/api/entities/tickets/tickets-api';
import {
  convertETSTicketsFiltersToQuery,
  getGetETSTicketsQueryKey,
  useETSTicketsQueryParams,
  useUploadETSAttachments,
} from 'gcs-common/clients/api/entities/tickets/tickets-api-helpers';
import { getErrorMsg } from 'gcs-common/helper/errorMessages';
import { downloadMessageFiles } from 'gcs-common/helper/mediaHelper';
import { getChannelCraftsmanConnections, getChannelTitle } from 'gcs-common/slices/channel/channelSelectors';
import { MAX_TICKET_ATTACHMENT_SIZE } from 'gcs-common/slices/tickets/ticketsConstants';
import { getChannelTicketFormPrefill, getTicketFormDraft } from 'gcs-common/slices/tickets/ticketsSelectors';
import { type TicketsState, clearFormPrefill, ticketFormChanged } from 'gcs-common/slices/tickets/ticketsSlice';
import type { Schema } from 'gcs-common/types/typeHelper';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import ErpCustomerDropDown from '../../../components/ErpCustomerDropDown/ErpCustomerDropDown';
import { showFailureNotification, showSuccessNotification } from '../../../slices/notifications/ShowNotificationThunk';
import TicketErrorModal from '../TicketAttachmentSizeErrorModal/TicketAttachmentSizeErrorModal';
import TicketAttachmentUpload from './TicketAttachmentUpload/TicketAttachmentUpload';
import TicketDiscardButton from './TicketDiscardButton/TicketDiscardButton';
import TicketInputChannelSelect from './TicketInputChannelSelect/TicketInputChannelSelect';
import styles from './styles.module.scss';

interface AutoSaveDraftProps {
  channelId?: string | undefined;
}
/**
 * Saves the current form values as draft.
 *
 * This is a separate react component so it can make use of the useFormikContext hook.
 */
const AutoSaveDraft = (props: AutoSaveDraftProps) => {
  const { channelId } = props;
  const dispatch = useDispatch();
  const { values } = useFormikContext<TicketsState['drafts'][string]>();

  useEffect(() => {
    dispatch(ticketFormChanged({ channelId, formValues: values }));
  }, [dispatch, channelId, values]);

  return <></>;
};

interface UpdateFormFieldsProps {
  channelId?: string | undefined;
}
/**
 * Updates the description and attachment field with selected messages.
 * This is not a one-time action, but user can select more messages,
 * even when they are already in the form.
 *
 * This is a separate react component so it can make use of the useFormikContext hook.
 */
const UpdateFormFields = (props: UpdateFormFieldsProps) => {
  const { channelId } = props;
  const { setValues } = useFormikContext();
  const dispatch = useDispatch();
  const prefill = useSelector(getChannelTicketFormPrefill(channelId || ''));

  const [showAttachmentSizeError, setShowAttachmentSizeError] = useState(false);

  useEffect(() => {
    (async () => {
      if (!prefill) return;

      let files: File[];
      if (prefill.files?.length) {
        files = await downloadMessageFiles(prefill.files);
        captureMessage('ETS Ticket Attachments - prefilling files', {
          extra: { channelId, files: files?.length },
          // @ts-ignore
          tags: ['ets'],
        });
      }

      setValues((currentValues: TicketsState['drafts'][string]) => {
        const newDescription = [currentValues.description, prefill.description].filter((x) => !!x).join('\n\n');

        let newAttachments = currentValues.attachments || [];

        const filteredFiles = files?.filter((file) => file.size <= MAX_TICKET_ATTACHMENT_SIZE);
        if (files && filteredFiles.length !== files.length) {
          captureMessage('ETS Ticket Attachments - prefilling files - file size limit', {
            extra: { channelId, files: files?.length, filteredFiles: filteredFiles?.length },
            // @ts-ignore
            tags: ['ets'],
          });
          setShowAttachmentSizeError(true);
        }

        if (filteredFiles?.length) {
          newAttachments = [...newAttachments, ...filteredFiles];
          captureMessage('ETS Ticket Attachments - prefilling files - merging files', {
            extra: {
              channelId,
              currentFiles: currentValues?.attachments?.length,
              filteredFiles: filteredFiles?.length,
              newAttachments: newAttachments?.length,
            },
            // @ts-ignore
            tags: ['ets'],
          });
        }

        return {
          ...currentValues,
          description: newDescription,
          attachments: newAttachments,
        };
      }, false)
        .then(() => {})
        .catch(() => {});

      // Clear prefill state to avoid adding it again
      dispatch(clearFormPrefill({ channelId }));
    })()
      .then(() => {})
      .catch(() => {});
  }, [prefill, channelId, dispatch, setValues]);

  return <>{showAttachmentSizeError && <TicketErrorModal onCancel={() => setShowAttachmentSizeError(false)} />}</>;
};

interface TicketFormProps {
  channelId?: string;
  onClose: VoidFunction;
  predefinedInputChannel?: Schema<'EtsInputChannelEnum'>;
}

const TicketForm = (props: TicketFormProps) => {
  const { channelId, onClose, predefinedInputChannel } = props;
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const channelConnections = useSelector(getChannelCraftsmanConnections(channelId));
  const channelConnectionId: string = channelConnections?.[0]?.id;
  const channelCustomerNumber: string = channelConnections?.[0]?.customerNumber;
  const channelTitle: string = useSelector(getChannelTitle(channelId));
  const channelName = channelId ? `BEEM - ${channelTitle}` : undefined;
  const draft = useSelector(getTicketFormDraft(channelId));
  const { etsTicketsfilters: filters } = useETSTicketsQueryParams();
  const createETSTicket = useCreateETSTicketMutation();
  const patchETSTicketStatus = usePatchETSTicketStatusMutation();
  const uploadETSAttachments = useUploadETSAttachments();
  const loading = createETSTicket.isPending || uploadETSAttachments.isPending || patchETSTicketStatus.isPending;

  const initialValues: TicketsState['drafts'][string] = {
    description: '',
    customerOrderText: '',
    attachments: [],
    erpCustomerId: channelConnectionId || '',
    inputChannel: predefinedInputChannel || undefined,
  };

  const validationSchema = Yup.object().shape({
    description: Yup.string().required('Erforderlich').matches(/\S/g, 'Erforderlich'),
    erpCustomerId: Yup.string().required('Erforderlich').matches(/\S/g, 'Erforderlich'),
    inputChannel: Yup.string().required('Erforderlich').matches(/\S/g, 'Erforderlich'),
  });

  useLayoutEffect(() => {
    if (channelId && !channelConnectionId) {
      dispatch(showFailureNotification(getErrorMsg('ets.create_ticket.no_connection_id')));
      onClose();
    }
  }, [dispatch, channelConnectionId, onClose, channelId]);

  const submit = async (values: TicketsState['drafts'][string]) => {
    const { description, customerOrderText, attachments, erpCustomerId, inputChannel } = values;
    if (!description || !erpCustomerId) {
      return;
    }
    captureMessage('ETS Ticket Attachments - creating ticket', {
      extra: { channelId, attachments: attachments?.length },
      // @ts-ignore
      tags: ['ets'],
    });

    // @ts-expect-error create title to display in the ets table and for legacy reasons
    const title = description.trim().split('\n')[0].slice(0, 256);
    let result: Awaited<ReturnType<typeof createETSTicket.mutateAsync>> | undefined;

    try {
      result = await createETSTicket.mutateAsync({
        body: {
          channelId,
          title,
          description,
          customerOrderText,
          erpCustomerId,
          inputChannel,
        },
      });
    } catch (_e) {
      dispatch(showFailureNotification(getErrorMsg('ets.create_ticket.default')));
      // @ts-expect-error sentry
      captureMessage('ETS Ticket - submitting failed', { extra: { channelId }, tags: ['ets'] });
    }

    // Request is successful
    if (result?.data) {
      if (attachments?.length) {
        // @ts-expect-error sentry
        captureMessage('ETS Ticket - uploading attachments', { extra: { channelId, result }, tags: ['ets'] });

        try {
          await uploadETSAttachments.uploadFiles(result.data.id, attachments);
          captureMessage('ETS Ticket - uploading attachments successful', {
            // @ts-ignore
            extra: { channelId, attachmentsResults },
            // @ts-ignore
            tags: ['ets'],
          });
        } catch {
          // Error uploading one or more attachments
        }
      }
      // @ts-expect-error sentry
      captureMessage('ETS Ticket - submitting successful', { extra: { channelId, result }, tags: ['ets'] });

      dispatch(showSuccessNotification('Ticket erstellt'));

      try {
        await patchETSTicketStatus.mutateAsync({
          path: {
            ticket_id: result.data.id,
          },
        });
      } catch (_e) {
        // Do nothing if patch fails. should be snyced from backend.
      }
      queryClient
        .invalidateQueries({
          queryKey: getGetETSTicketsQueryKey(convertETSTicketsFiltersToQuery(filters)),
        })
        .then(() => {})
        .catch(() => {});

      dispatch(ticketFormChanged({ channelId }));

      onClose();
    }
  };

  return (
    <Formik<TicketsState['drafts'][string]>
      /**
       * Form values are channel specific.
       * We need to make sure to re-render the form when the channel id changes,
       * otherwise the values of one channel might override the draft of another channel.
       */
      key={channelId || 'no-channel'}
      initialValues={draft || initialValues}
      validationSchema={validationSchema}
      onSubmit={submit}
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <div className={styles.ticketFormWrapper}>
            <AutoSaveDraft channelId={channelId} />
            <UpdateFormFields channelId={channelId} />

            <div className={styles.formHeader}>Neue Ersatzteilanfrage</div>
            <div className={styles.formDetails}>
              <div className={styles.formElement}>
                <div className={styles.formElementTitle}>
                  Beschreibung
                  <span className={styles.required}>&nbsp;*</span>
                </div>
                <div>
                  <Field className={styles.textArea} name="description" as="textarea" />
                  {errors.description ? <div className={styles.errorText}>{errors.description}</div> : null}
                </div>
              </div>
              <div className={styles.formElement}>
                <div className={styles.formElementTitle}>Anhänge</div>
                <div>
                  <TicketAttachmentUpload />
                </div>
              </div>
              <div className={styles.formElement}>
                <div className={styles.formElementTitle}>
                  Kommission
                  <span className={styles.optional}>&nbsp;(Optional)</span>
                </div>
                <Field className={styles.textbox} name="customerOrderText" type="text" />
              </div>
              {channelCustomerNumber ? (
                <div className={`${styles.formElement} ${styles.disabled}`}>
                  <div className={styles.formElementTitle}>Kundennummer</div>
                  <input
                    className={styles.textbox}
                    name="erpCustomerId"
                    type="text"
                    value={channelCustomerNumber}
                    disabled
                  />
                </div>
              ) : (
                <div className={`${styles.formElement}`}>
                  <div className={styles.formElementTitle}>
                    Kundennummer
                    <span className={styles.required}>&nbsp;*</span>
                  </div>
                  <ErpCustomerDropDown
                    value={values.erpCustomerId}
                    onChange={(erpCustomerId) => {
                      setFieldValue('erpCustomerId', erpCustomerId)
                        .then(() => {})
                        .catch(() => {});
                    }}
                    placeholder="Kunde auswählen"
                  />
                  {errors.erpCustomerId && touched.erpCustomerId ? (
                    <div className={styles.errorText}>{errors.erpCustomerId}</div>
                  ) : null}
                </div>
              )}
              {!predefinedInputChannel && (
                <div className={styles.formElement}>
                  <div className={styles.formElementTitle}>
                    Eingangskanal
                    <span className={styles.required}>&nbsp;*</span>
                  </div>
                  <div>
                    <TicketInputChannelSelect
                      onChange={(value: Schema<'EtsInputChannelEnum'> | undefined) => {
                        setFieldValue('inputChannel', value)
                          .then(() => {})
                          .catch(() => {});
                      }}
                    />
                    {errors.inputChannel && touched.inputChannel ? (
                      <div className={styles.errorText}>{errors.inputChannel}</div>
                    ) : null}
                  </div>
                </div>
              )}
              {channelName && (
                <div className={`${styles.formElement} ${styles.disabled}`}>
                  <div className={styles.formElementTitle}>Eingangskanal</div>
                  <input className={styles.textbox} name="inputChannel" type="text" value={channelName} disabled />
                </div>
              )}
              <div className={styles.buttonWrapper}>
                <TicketDiscardButton
                  channelId={channelId}
                  onClose={onClose}
                  disabled={loading}
                  initialValues={initialValues}
                />
                <button disabled={loading} type="submit" className={styles.createTicket}>
                  Erstellen
                </button>
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TicketForm;
